import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'
import {
    PaymentMethodCreateRequest, PaymentMethodPatchInBatchRequest,
    PaymentMethodResponse
} from '@/modules/configurationModule/detail/accounting/billable/payment-method/services/payment-method.dto'
import PaymentMethodModal
    from '@/modules/configurationModule/detail/accounting/billable/payment-method/components/payment-method-modal/payment-method-modal.vue'
import {
    initialPaymentMethodGridState, PaymentMethodGridState
} from '@/modules/configurationModule/detail/accounting/billable/payment-method/components/payment-method-grid/payment-method-grid.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        GridDisplayOptions,
        PaymentMethodModal
    }
})
export default class PaymentMethodGrid extends BaseCrudServerSideComponent<PaymentMethodResponse> implements BaseCrud {
    @Ref() readonly paymentMethodModalWrapper!: any

    state: PaymentMethodGridState
    constructor() {
        super()
        this.state = initialPaymentMethodGridState()
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(paymentMethod: PaymentMethodResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (paymentMethod) {
            this.showModal(this.$tc('configuration.actions.update'), paymentMethod, this.paymentMethodModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assay => assay.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.paymentMethodModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, paymentMethod: PaymentMethodResponse[]): Promise<void> {
        if (paymentMethod?.length) {
            const batchRequests = paymentMethod.filter(paymentMethod => paymentMethod.active !== active)
                .map(paymentMethod => ({
                    id: paymentMethod.id,
                    active
                }))
            if (await this._patchPaymentMethods(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: PaymentMethodCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createPaymentMethod(createRequest)) {
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: PaymentMethodPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchPaymentMethods([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deletePaymentMethods(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    // SERVICE CALLS ///

    private async _createPaymentMethod(createRequest: PaymentMethodCreateRequest): Promise<StrictNullable<PaymentMethodResponse>> {
        const response = await this.$_toCallUnique(this.state.service.create(createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.paymentMethodModalWrapper.hide()
        }
        return response
    }

    private async _patchPaymentMethods(batchRequests: PaymentMethodPatchInBatchRequest[]): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const response = await this.$_toCallUnique(this.state.service.patch(batchRequests))
        if (response && response.length !== batchRequests.length) {
            const elements = batchRequests.length - response.length
            if (elements > 0) {
                const choice = elements > 1 ? 2 : 1
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
            }
            this.paymentMethodModalWrapper.hide()
        }
        return response
    }

    private async _deletePaymentMethods(requestDelete: EntityIdentifiersRequest): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const ids = requestDelete?.entityIdentifiers
        const response = await this.$_toCallUnique(this.state.service.delete(requestDelete))
        if (response) {
            const elements = ids.length - response.length
            if (elements > 0) {
                const choice = elements > 1 ? 2 : 1
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
            }
        }
        return response
    }
}
